export type NavItem = { label: string; href: string }
export type Capability = { id: string; name: string; statement: string; description: string }
export type ProjectMedia = { src: string; alt: string }
export type Project = {
  slug: string
  client: string
  title: string
  category: string
  image: ProjectMedia
  caseStudy?: {
    intro: string
    year: string
    platform: string
    engine: string
    scope: string
    technicalConstraints: string
    results: string
    metrics: string
  }
}
export type DuoPoseName = 'closed' | 'open' | 'experience' | 'seated'
export type DuoUseCase = { name: string; description: string; screen: ScreenConcept }
export type ScreenConcept = 'apps' | 'games' | 'entertainment' | 'automotive' | 'retail' | 'sports' | 'productivity'

export const navigation: NavItem[] = [
  { label: 'Work', href: '/work/' },
  { label: 'iPhone Duo', href: '/iphone-duo/' },
  { label: 'About', href: '/about/' },
  { label: 'Contact', href: '/contact/' },
]

export const brandLogos = [
  { name: 'Nickelodeon', src: '/images/nickelodeon.svg' },
  { name: 'Ford', src: '/images/ford.svg' },
  { name: 'HP', src: '/images/hp.svg' },
  { name: 'Breitling', src: '/images/breitling.svg' },
  { name: 'Budweiser', src: '/images/budweiser.svg' },
  { name: 'Honda', src: '/images/honda.svg' },
  { name: 'Mattel', src: '/images/mattel.svg' },
  { name: 'Neon', src: '/images/neon.svg' },
]

export const brands = brandLogos.map(brand => brand.name)

export const capabilities: Capability[] = [
  { id: 'apps', name: 'Apps', statement: 'Complete products, built end to end.', description: 'Strategy, experience design and engineering for native and cross-platform products — from the first idea through launch and growth.' },
  { id: 'ai', name: 'AI', statement: 'Intelligence people can use.', description: 'AI-powered apps, services and product features designed around real people, useful workflows and measurable outcomes.' },
  { id: 'games', name: 'Unity Games', statement: 'High quality. High performance.', description: 'Unity 2D and 3D game development for mobile, desktop and console, combining production-quality visuals, responsive gameplay and performance optimized for each target platform.' },
  { id: 'immersive', name: 'Immersive', statement: 'Experiences people feel.', description: 'New forms of interaction combining devices, media, realtime technology and the physical world.' },
]

export const projects: Project[] = [
  {
    slug: 'spongebob-tickler',
    client: 'Nickelodeon',
    title: 'SpongeBob Tickler',
    category: 'Game',
    image: { src: '/images/sbob.webp', alt: 'SpongeBob Tickler for Nickelodeon' },
    caseStudy: {
      intro: 'SpongeBob Tickler is a game project created by Boostpad for Nickelodeon. TODO: Confirm the production year, target platform and Boostpad’s exact scope before publication.',
      year: 'TODO: Confirm production year',
      platform: 'TODO: Confirm target platform',
      engine: 'TODO: Confirm engine and core technologies',
      scope: 'Boostpad built SpongeBob Tickler for Nickelodeon as a game project. TODO: Add the approved product summary and verified production scope.',
      technicalConstraints: 'TODO: Document the verified platform constraints, performance targets, input model and Nickelodeon brand requirements.',
      results: 'TODO: Add approved launch results and the client outcome.',
      metrics: 'TODO: Add verified downloads, sessions, engagement or other approved metrics if available.',
    },
  },
  {
    slug: 'avatar-the-way-of-water',
    client: 'Neon',
    title: 'Avatar: The Way of Water',
    category: 'Immersive experience',
    image: { src: '/images/avatar2.webp', alt: 'Avatar: The Way of Water immersive experience for Neon' },
    caseStudy: {
      intro: 'Avatar: The Way of Water is an immersive experience project created by Boostpad for Neon. TODO: Confirm the production year, installation platform and Boostpad’s exact scope before publication.',
      year: 'TODO: Confirm production year',
      platform: 'TODO: Confirm installation and target platform',
      engine: 'TODO: Confirm engine and media pipeline',
      scope: 'Boostpad built an immersive experience project for Neon connected to Avatar: The Way of Water. TODO: Add the approved experience summary and verified production scope.',
      technicalConstraints: 'TODO: Document the verified installation, realtime rendering, hardware, media and venue constraints.',
      results: 'TODO: Add approved launch, audience and client outcomes.',
      metrics: 'TODO: Add verified attendance, interactions, dwell time or other approved metrics if available.',
    },
  },
  {
    slug: 'bud-cup-6v6',
    client: 'Budweiser',
    title: 'Bud CUP 6v6',
    category: 'Advergame',
    image: { src: '/images/budcup6v6.webp', alt: 'Bud CUP 6v6 advergame for Budweiser' },
    caseStudy: {
      intro: 'Bud CUP 6v6 is an advergame project created by Boostpad for Budweiser. TODO: Confirm the production year, target platform and Boostpad’s exact scope before publication.',
      year: 'TODO: Confirm production year',
      platform: 'TODO: Confirm target platform',
      engine: 'TODO: Confirm engine and backend services',
      scope: 'Boostpad built Bud CUP 6v6 for Budweiser as an advergame project. TODO: Add the approved gameplay summary and verified production scope.',
      technicalConstraints: 'TODO: Document the verified gameplay, platform, multiplayer, campaign and brand constraints.',
      results: 'TODO: Add approved campaign and audience outcomes.',
      metrics: 'TODO: Add verified players, matches, engagement or other approved metrics if available.',
    },
  },
  {
    slug: 'kuga-presentation',
    client: 'Ford',
    title: 'Kuga Presentation',
    category: 'Showcase & Customization',
    image: { src: '/images/kuga.webp', alt: 'Kuga Presentation for Ford' },
  },
  {
    slug: 'tourbillon-watch',
    client: 'Breitling',
    title: 'Tourbillon Watch',
    category: 'Showcase & Customization',
    image: { src: '/images/breitling.webp', alt: 'Tourbillon Watch showcase for Breitling' },
  },
]

export const duoServices = [
  { index: '01', name: 'Adapt', title: 'Adapt your existing iPhone app for Duo.', description: 'Senior UX review, adaptive layouts, technical implementation, production testing and launch support.' },
  { index: '02', name: 'Create', title: 'Build a new iPhone Duo app or product.', description: 'End-to-end product strategy, experience design and engineering built around the foldable form factor.' },
  { index: '03', name: 'Play', title: 'Create iPhone Duo games and immersive experiences.', description: 'Unity 2D and 3D development, branded games, interactive entertainment and high-performance realtime experiences built to production quality.' },
]

export const duoFaqs = [
  {
    question: 'What can Boostpad build for iPhone Duo?',
    answer: 'Boostpad designs and develops complete iPhone Duo products, native apps, games and immersive experiences, from product strategy and prototyping through production engineering and launch.',
  },
  {
    question: 'Can an existing iPhone app be adapted for iPhone Duo?',
    answer: 'Yes. Our Duo Experience Review identifies adaptive-layout requirements, technical constraints and new dual-screen opportunities, then turns them into an implementation roadmap.',
  },
  {
    question: 'Why work with Boostpad on an iPhone Duo project?',
    answer: 'Our senior team has built for Apple platforms since the first iPhone SDK in 2008 and combines product strategy, design, native app engineering, games and realtime 3D in one production team.',
  },
  {
    question: 'Does Boostpad provide production-ready iPhone Duo development?',
    answer: 'Yes. We work from concept and prototype through implementation, quality assurance, performance optimization and launch support for production-quality iPhone Duo experiences.',
  },
]

export const homeFaqs = [
  {
    question: 'What kinds of projects does Boostpad take on?',
    answer: 'Boostpad takes on complete digital platforms and products, mobile and desktop apps, AI implementation and services, games, realtime 3D and immersive brand experiences.',
  },
  {
    question: 'How does Boostpad implement AI in digital products?',
    answer: 'Boostpad designs and implements production-ready AI within complete digital products, including intelligent features, assistants, workflow automation, data integrations and custom AI services.',
  },
  {
    question: 'Can Boostpad build a complete digital platform?',
    answer: 'Yes. Boostpad builds complete digital platforms end to end, covering product strategy, UX and UI, frontend and backend engineering, infrastructure, AI integration, quality assurance, deployment and ongoing evolution.',
  },
  {
    question: 'Which platforms does Boostpad work on?',
    answer: 'Boostpad works across Apple platforms, mobile, desktop, console, web and emerging device categories, selecting the production stack around each product’s requirements.',
  },
  {
    question: 'Does Boostpad develop games with Unity?',
    answer: 'Yes. Boostpad develops Unity 2D and 3D games with high production quality and optimized performance on each target platform.',
  },
  {
    question: 'Where is Boostpad located?',
    answer: 'Boostpad is based in Buenos Aires and California and works with clients worldwide.',
  },
  {
    question: 'How does a Boostpad project work?',
    answer: 'A Boostpad project brings senior product strategy, experience design and engineering into one team, moving from definition and prototyping through production, testing and launch.',
  },
]

export const duoStates: Array<{ pose: DuoPoseName; label: string; statement: string }> = [
  { pose: 'closed', label: 'Closed', statement: 'Familiar when it needs to be.' },
  { pose: 'open', label: 'Open', statement: 'More room when it matters.' },
  { pose: 'seated', label: 'Seated', statement: 'A completely different way to interact.' },
]

export const duoUseCases: DuoUseCase[] = [
  { name: 'Entertainment', description: 'Content, context and controls sharing the same experience.', screen: 'entertainment' },
  { name: 'Retail', description: 'Explore, compare and configure without constant navigation.', screen: 'retail' },
  { name: 'Automotive', description: 'Rich configurators and interactive product experiences.', screen: 'automotive' },
  { name: 'Sports', description: 'Live content, stats and interaction simultaneously.', screen: 'sports' },
  { name: 'Productivity', description: 'More information without forcing a desktop interface onto a phone.', screen: 'productivity' },
  { name: 'Games', description: 'Gameplay and secondary information redesigned around new device states.', screen: 'games' },
]

export const duoTimeline = [
  { year: '2008', title: 'Apple releases the first iPhone SDK.', description: 'Our team begins developing apps and games for iPhone.' },
  { year: '2026', title: 'iPhone Duo changes how an iPhone can be used.', description: "We're building for it from day one." },
]
